// queries/useGetAllModels.ts
"use client";
import fetchData from "libs/fetch"; // Correct import
import { GET_ALL_MODELS, GET_USER_MODELS } from "api";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ResponseModel } from "types";

/**
 * Fetch models with conditional Authorization header.
 *
 * @param endpoint - The API endpoint.
 * @param pageParam - Current page number.
 * @param pageSize - Number of models per page.
 * @param tags - Array of tags for filtering.
 * @param sortType - Sorting type.
 * @param filterType - Filtering type.
 * @param includeAuth - Whether to include the Authorization header.
 * @returns The response containing models.
 */
const generalizedFetchModels = async (
  endpoint: string,
  pageParam: number = 1, 
  pageSize: number,
  tags: string[],
  sortType?: string,
  filterType?: string,
  includeAuth: boolean = true 
): Promise<ResponseModel> => {
  const tagsQueryParams = tags.map((tag) => `model_tag=${encodeURIComponent(tag)}`).join("&");
  const sortQueryParam = sortType ? `&popular=${sortType}` : "";
  const filterQueryParam = filterType ? `&model_category_name=${filterType}` : "";

  const queryString = `page=${pageParam}&page_size=${pageSize}${
    tagsQueryParams ? `&${tagsQueryParams}` : ""
  }${sortQueryParam}${filterQueryParam}`;

  return fetchData(`${endpoint}?${queryString}`, {}, includeAuth);
};

export const useGetAllModels = (
  isUserModels: boolean,
  pageSize: number,
  tags: string[],
  sortType?: boolean,
  filterType?: string
) => {
  const endpoint = isUserModels ? GET_USER_MODELS : GET_ALL_MODELS;
  const includeAuth = isUserModels; 

  return useInfiniteQuery<ResponseModel, Error>({
    queryKey: [endpoint, pageSize, tags, sortType, filterType],
    queryFn: ({ pageParam = 1 }) =>
      //@ts-ignore
      generalizedFetchModels(endpoint, pageParam as number, pageSize, tags, sortType, filterType, includeAuth), // Cast pageParam to number
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages?.length + 1;
      return nextPage <= Math.ceil(lastPage.total_models / pageSize) ? nextPage : undefined;
    },
    initialPageParam: 1,
  });
};

export default useGetAllModels;
